import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect
} from "react";

const MediaPlayerContext = createContext();

export const useMediaPlayer = () => useContext(MediaPlayerContext);

export const MediaPlayerProvider = ({ children, currentTracks }) => {
  const [tracks, setTracks] = useState(currentTracks);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loop, setLoop] = useState(false);
  const [loadingAudioBuffers, setLoadingAudioBuffers] = useState(false); // [1
  const [audioBuffers, setAudioBuffers] = useState([]);
  const audioRef = useRef(new Audio());
  const trackSource = useRef();
  const audioContext = useRef(new AudioContext());
  const initialized = useRef(false);

  useEffect(() => {
    setTracks(currentTracks);
  }, [currentTracks]);

  useEffect(() => {
    audioRef.current.src = tracks[currentTrackIndex].audioSrc;

    const handleSongEnd = () => {
      if (currentTrackIndex < tracks.length - 1) {
        setCurrentTrackIndex(currentTrackIndex + 1);
      } else {
        setPlaying(false);
      }
    };

    audioRef.current.addEventListener("ended", handleSongEnd);

    return () => {
      audioRef.current.removeEventListener("ended", handleSongEnd);
    };
  }, [currentTrackIndex, tracks.length]);

  useEffect(() => {
    if (playing && initialized.current) {
      audioContext.current.resume();
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  }, [playing, currentTrackIndex]);

  useEffect(() => {
    preloadAudioBuffers();
  }, [tracks]);

  const preloadAudioBuffers = async () => {
    setLoadingAudioBuffers(true);
    const baseURL = window.location.origin; // Obtient l'URL de base

    let buffers = [];

    for (const track of tracks) {
      try {
        const response = await fetch(`${baseURL}${track.audioSrc}`);
        const arrayBuffer = await response.arrayBuffer();
        const decodedAudio = await audioContext.current.decodeAudioData(
          arrayBuffer
        );
        buffers.push(decodedAudio);
      } catch (error) {
        console.error("Error loading audio:", error);
      }
    }

    setAudioBuffers(buffers);

    setLoadingAudioBuffers(false);
    setAudioBuffers(buffers.filter((buffer) => buffer !== null));
  };

  const initUserGesture = () => {
    if (initialized.current) {
      return;
    }

    initialized.current = true;
    audioContext.current = audioContext.current || new AudioContext();
    if (!trackSource.current) {
      trackSource.current = audioContext.current.createMediaElementSource(
        audioRef.current
      );
      trackSource.current.connect(audioContext.current.destination);
    }
  };

  const handlePlayPause = () => {
    initUserGesture();
    const newPlayingState = !playing;
    setPlaying(newPlayingState);
  };

  const handlePlay = () => {
    initUserGesture();
    setPlaying(true);
  };

  const handlePause = () => {
    initUserGesture();
    setPlaying(false);
  };

  const playSong = (trackIndex) => {
    initUserGesture();
    setCurrentTrackIndex(trackIndex);
    setPlaying(true);
  };

  const handleNext = () => {
    initUserGesture();
    if (currentTrackIndex < tracks.length - 1) {
      setCurrentTrackIndex(currentTrackIndex + 1);
    } else {
      setCurrentTrackIndex(0);
    }
  };

  const handlePrevious = () => {
    initUserGesture();
    const currentTime = audioRef.current.currentTime;

    if (currentTime > 1.5) {
      audioRef.current.currentTime = 0;
      return;
    }

    if (currentTrackIndex > 0) {
      setCurrentTrackIndex(currentTrackIndex - 1);
    } else {
      setCurrentTrackIndex(tracks.length - 1);
    }
  };

  const toggleLoop = () => {
    const newLoopState = !loop;
    setLoop(newLoopState);
    audioRef.current.loop = newLoopState;
  };

  const contextValue = {
    tracks,
    currentTrackIndex,
    setCurrentTrackIndex,
    playing,
    setPlaying,
    loop,
    setLoop,
    handlePlayPause,
    handlePlay,
    handlePause,
    handleNext,
    handlePrevious,
    toggleLoop,
    playSong,
    audioRef,
    trackSource,
    audioNode: trackSource.current,
    audioContext,
    audioBuffers,
    initUserGesture,
    loadingAudioBuffers
  };

  return (
    <MediaPlayerContext.Provider value={contextValue}>
      {children}
    </MediaPlayerContext.Provider>
  );
};
